import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Loader2, ArrowRight } from "lucide-react";
import AddressInput from "@/shared/components/AddressInput";
import { api } from "@/shared/lib/api";

/**
 * InstantEstimate — a small "see your weekly price" surface on /welcome.
 *
 * Collapsed: a thin pill labelled "See your weekly estimate".
 * Expanded: a glassmorphism panel with two AddressInputs and a Run button.
 *
 * On Run we hit /geocode/distance (server-side Google Routes) and then
 * /pricing/quote to receive ONLY the final daily + weekly numbers. The
 * full pricing matrix (base price, per-mile rate, discount table) is
 * intentionally absent from both responses — this surface is designed
 * so curious prospects can self-quote without ever seeing the formula
 * the company uses to price routes.
 *
 * On the result panel we offer "Continue to schedule" — this stashes the
 * priced pickup/destination in localStorage under `link.preset.route`
 * and navigates the prospect to /onboarding. After signup + verify,
 * Onboarding redirects to /schedule and ScheduleSetup hydrates the
 * preset into the booking pill so the rider lands one tap away from
 * Slide-to-Pay.
 */
const PRESET_KEY = "link.preset.route";

export default function InstantEstimate({ visible, delay = 940 }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const reqRef = useRef(0);

  const reset = () => {
    setPickup("");
    setDestination("");
    setResult(null);
    setError("");
    setBusy(false);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const run = async () => {
    const o = (pickup || "").trim();
    const d = (destination || "").trim();
    if (!o || !d) {
      setError("Add both a pickup and a destination.");
      return;
    }
    const myToken = ++reqRef.current;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data: dist } = await api.get("/geocode/distance", {
        params: { origin: o, destination: d },
      });
      if (myToken !== reqRef.current) return;
      if (!dist?.supported) {
        setBusy(false);
        setError(`That route is outside our service range (max ${dist?.max_miles ?? 50} mi one-way).`);
        return;
      }
      const { data: q } = await api.get("/pricing/quote", {
        params: { distance_miles: dist.miles, days: 5 },
      });
      if (myToken !== reqRef.current) return;
      if (!q?.supported) {
        setBusy(false);
        setError(q?.message || "We can't price this route right now.");
        return;
      }
      setResult({
        miles: dist.miles,
        daily: Number(q.daily_price || 0),
        weekly: Number(q.weekly_total || 0),
      });
    } catch (e) {
      if (myToken !== reqRef.current) return;
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We couldn't reach the routing service. Try again.");
    } finally {
      if (myToken === reqRef.current) setBusy(false);
    }
  };

  const continueToSchedule = () => {
    try {
      localStorage.setItem(
        PRESET_KEY,
        JSON.stringify({
          pickup: pickup.trim(),
          destination: destination.trim(),
          ts: Date.now(),
        }),
      );
    } catch (_) {
      // localStorage may be disabled (private mode). The CTA still
      // lands the rider in onboarding; they'll just re-enter the
      // addresses once they hit /schedule.
    }
    nav("/onboarding");
  };

  const baseStyle = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(14px)",
    transition: `opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  };

  return (
    <div className="px-7 sm:px-10 mt-3 max-w-xl mx-auto flex justify-center" style={baseStyle}>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0A1128]/15 dark:border-white/15 bg-white/70 dark:bg-white/5 hover:bg-white/90 dark:hover:bg-white/10 hover:border-[#ed6500]/60 backdrop-blur-md transition-colors text-[11.5px] tracking-[0.16em] uppercase font-semibold text-[#0A1128] dark:text-[#F9F7F4]/90 shadow-sm"
          data-testid="welcome-instant-quote-chip"
        >
          <Sparkles size={13} className="text-[#ed6500]" />
          See your weekly estimate
          <ArrowRight size={13} className="text-[#ed6500] transition-transform group-hover:translate-x-0.5" />
        </button>
      ) : (
        <div
          className="rounded-2xl border border-white/15 bg-[#0A1128]/70 backdrop-blur-xl p-4 sm:p-5 text-[#F9F7F4]"
          data-testid="welcome-instant-quote-panel"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">
                Instant estimate
              </div>
              <div className="text-[13px] text-[#F9F7F4]/75 mt-1 leading-snug">
                Two addresses. One number. No sign-up.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[#F9F7F4]/60 hover:text-[#ed6500] p-1 -mr-1"
              aria-label="Close estimate"
              data-testid="welcome-instant-quote-close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="space-y-2.5">
            <AddressInput
              value={pickup}
              onChange={setPickup}
              placeholder="Pickup address"
              allowCurrentLocation={false}
              showSavedPlaces={false}
              variant="dark"
              testId="welcome-instant-quote-pickup"
            />
            <AddressInput
              value={destination}
              onChange={setDestination}
              placeholder="Destination address"
              allowCurrentLocation={false}
              showSavedPlaces={false}
              variant="dark"
              testId="welcome-instant-quote-destination"
            />
          </div>

          <button
            type="button"
            onClick={run}
            disabled={busy || !pickup || !destination}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#ed6500] hover:bg-[#ff8a36] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[12px] tracking-[0.18em] uppercase font-semibold transition-colors"
            data-testid="welcome-instant-quote-run"
          >
            {busy ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Pricing…
              </>
            ) : (
              <>
                Show my estimate
                <ArrowRight size={14} />
              </>
            )}
          </button>

          {error && (
            <div
              className="mt-3 text-[12px] text-[#ff8a36] bg-[#ff8a36]/10 border border-[#ff8a36]/30 rounded-lg px-3 py-2"
              data-testid="welcome-instant-quote-error"
            >
              {error}
            </div>
          )}

          {result && (
            <div
              className="mt-4 rounded-xl bg-[#F9F7F4]/5 border border-white/10 p-4"
              data-testid="welcome-instant-quote-result"
            >
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#F9F7F4]/55 mb-2">
                Your route · {result.miles} mi one-way
              </div>
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#F9F7F4]/55">
                    Per day
                  </div>
                  <div
                    className="font-serif text-[28px] leading-none mt-1 tabular-nums"
                    data-testid="welcome-instant-quote-daily"
                  >
                    ${result.daily.toFixed(2)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#ed6500]">
                    Per week · 5 days
                  </div>
                  <div
                    className="font-serif text-[28px] leading-none mt-1 text-[#ed6500] tabular-nums"
                    data-testid="welcome-instant-quote-weekly"
                  >
                    ${result.weekly.toFixed(2)}
                  </div>
                </div>
              </div>
              <div className="text-[11.5px] text-[#F9F7F4]/55 mt-3 leading-snug">
                Final price, no surge or per-ride fees. Pick fewer days at checkout and your weekly drops accordingly.
              </div>

              <button
                type="button"
                onClick={continueToSchedule}
                className="group mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#ed6500] hover:bg-[#ff8a36] text-white text-[12px] tracking-[0.18em] uppercase font-semibold transition-colors"
                data-testid="welcome-instant-quote-continue"
              >
                Continue to schedule
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </button>
              <div className="text-[10.5px] text-[#F9F7F4]/45 mt-2 text-center tracking-wide">
                We'll carry your route into onboarding.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
